import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ICreateConnectorCloudFunction } from './types';
import { LocalAdAccount } from '@/types/Connectors';
import { UserRole } from '@/types/types';

export const firebaseConfig = {
  apiKey: import.meta.env._VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env._VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env._VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env._VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env._VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env._VITE_FIREBASE_APP_ID,
  // measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};
type EntityType = 'workspaces' | 'organizations';

export interface ChangeConnectorStatePayload {
  connectorName: string;
  connectorId: string;
  workspaceId: string;
  organizationId: string;
  region: string;
}

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const functions = getFunctions(app);

functions.region = 'europe-west6';

export const acceptInvitationCloudFunction = httpsCallable<{
  entityType: EntityType;
  entityId: string;
  invitationId: string;
}>(functions, 'acceptInvitation');

export const createUserCloudFunction = httpsCallable<{
  displayName?: string;
}>(functions, 'createUser');

export const createWorkspaceCloudFunction = httpsCallable<
  {
    workspaceName: string;
    organizationId: string;
  },
  string
>(functions, 'createWorkspace');
export const createInvitationCloudFunction = httpsCallable<{
  entityId: string;
  email: string;
  entityType: EntityType;
  role: string;
}>(functions, 'createInvitation');
export const createInvitationsCloudFunction = httpsCallable<{
  entityId: string;
  emails: string[];
  entityType: EntityType;
  role: string;
}>(functions, 'createInvitations');
export const deleteWorkspaceCloudFunction = httpsCallable<{
  workspaceId: string;
  organizationId: string;
  region: string;
}>(functions, 'deleteWorkspace');
export const deleteUserAccountCloudFunction = httpsCallable(
  functions,
  'deleteUserAccount',
);

export const generateLoginTokenCloudFunction = httpsCallable<{
  workspaceId: string;
  platform: string;
}>(functions, 'generateLoginToken');

/**
 * Creates connector on an organization level and sends pubsub message to setup connector in GCP
 *
 * @param {ICreateConnectorCloudFunction} data - Data required to create organization level connector.
 */
export const createConnectorCloudFunction = httpsCallable<
  ICreateConnectorCloudFunction,
  { isCreatingOrganizationConnector: boolean }
>(functions, 'createConnector');

export const deleteConnectorCloudFunction = httpsCallable<{
  connectorId: string;
  organizationId: string;
  region: string;
  workspaceId: string;
}>(functions, 'deleteConnector');

export const updateAdAccountsCloudFunction = httpsCallable<{
  workspaceId: string;
  accounts: LocalAdAccount[];
  organizationId: string;
  region: string;
  connectorId: string;
}>(functions, 'updateAdAccounts');

export const activateConnectorCloudFunction = httpsCallable<{
  workspaceId: string;
  connectorId: string;
  organizationId: string;
  region: string;
  timezone?: string;
  locationCode?: number;
}>(functions, 'activateConnector');

export const deactivateConnectorCloudFunction = httpsCallable<{
  workspaceId: string;
  connectorId: string;
  organizationId: string;
  region: string;
}>(functions, 'deactivateConnector');

export const updateShopCloudFunction = httpsCallable<{
  workspaceId: string;
  shop: string;
  connectorId: string;
  organizationId: string;
  region: string;
}>(functions, 'updateShop');

export const deleteConnectorForReconnectionClopudFunction = httpsCallable<{
  connectorId: string;
  organizationId: string;
  region: string;
  workspaceId: string;
}>(functions, 'deleteConnectorForReconnection');

/**
 * Cloud function to create an organization.
 *
 * @param organizationName - The name of the organization.
 * @param region - The region of the organization.
 */
export const createOrganizationCloudFunction = httpsCallable<
  {
    organizationName: string;
    region: string;
    priceId: string;
  },
  string
>(functions, 'createOrganization');

/**
 * Cloud function to delete an organization.
 *
 * @param organizationId - The ID of the organization.
 * @param region - The region of the organization.
 */
export const deleteOrganizationCloudFunction = httpsCallable<{
  organizationId: string;
  region: string;
}>(functions, 'deleteOrganization');

/**
 * Cloud function to handle an invitation link.
 *
 * @param hash - The hash of the invitation link.
 * @param invitationType - The type of the invitation.
 */
export const handleInvitationLinkCloudFunction = httpsCallable<{
  hash: string;
  invitationType: string;
}>(functions, 'handleInvitationLink');

/**
 * Cloud function to get data from an invitation link.
 *
 * @param hash - The hash of the invitation link.
 * @param invitationType - The type of the invitation.
 */
export const getInvitationLinkDataCloudFunction = httpsCallable<{
  hash: string;
  invitationType: string;
}>(functions, 'getInvitationLinkData');

/**
 * Cloud function to restart an organization.
 *
 * @param organizationId - The ID of the organization.
 */
export const restartOrganizationCloudFunction = httpsCallable<
  {
    organizationId: string;
  },
  string
>(functions, 'restartOrganization');

/**
 * Cloud function to restart the creation of a connector.
 *
 * @param connectorId - The ID of the connector.
 * @param workspaceId - The ID of the workspace.
 * @param organizationId - The ID of the organization.
 */
export const restartConnectorCreationCloudFunction = httpsCallable<{
  connectorId: string;
  workspaceId: string;
  organizationId: string;
}>(functions, 'restartConnectorCreation');

/**
 * Cloud function to generate a Streamlit link.
 *
 * @param workspaceId - The ID of the workspace.
 * @param organizationId - The ID of the organization.
 * @param report - The optional report parameter.
 * @returns The generated Streamlit link.
 */
export const generateStreamlitLinkCloudFunction = httpsCallable<
  {
    workspaceId: string;
    organizationId: string;
    report?: string;
  },
  string
>(functions, 'generateStreamlitLink');

/**
 * Cloud function to delete an ad account.
 *
 * @param workspaceId - The ID of the workspace.
 * @param connectorId - The ID of the connector.
 * @param accountId - The ID of the ad account.
 */
export const deleteAdAccountCloudFunction = httpsCallable<{
  workspaceId: string;
  connectorId: string;
  accountIds: string[];
}>(functions, 'deleteAdAccount');

export const resendInvitationEmailCloudFunction = httpsCallable<{
  hash: string;
  entityType: 'workspace' | 'organization';
}>(functions, 'resendEmail');

export const enableDataforseoConnectorForWorkspaceCloudFunction = httpsCallable<{
  workspaceId: string;
  connectorId: string;
}>(functions, 'enableDataforseoConnectorForWorkspace');

export const generateCustomerPortalLinkCloudFunction = httpsCallable<{
  organizationId: string;
}>(functions, 'generateCustomerPortalLink');
/**
 * Generates stripe customer portal link for user
 *
 */
export const generateCustomerPortalLinkForUserCloudFunction = httpsCallable<
  undefined,
  string
>(functions, 'generateCustomerPortalLinkForUser');

export const createCheckoutSessionCloudFunction = httpsCallable<
  {
    organizationId: string;
    priceId: string;
    mode: string;
    sourceUrl: string;
  },
  { url: string }
>(functions, 'createCheckoutSession');

/**
 * Deletes users from specific workspace
 *
 * @param workspaceId - The ID of the workspace.
 * @param usersIds - Ids of users to delete.
 */
export const deletePermissionsCloudFunction = httpsCallable<{
  workspaceId: string;
  usersIds: string[];
}>(functions, 'deletePermissions');

/**
 * Deletes users from specific organization and it's workspaces
 *
 * @param organizationId - The ID of the workspace.
 * @param usersIds - Ids of users to delete.
 */
export const deleteOrganizationPermissionsCloudFunction = httpsCallable<{
  organizationId: string;
  usersIds: string[];
}>(functions, 'deleteOrganizationPermissions');

export const changeOrganizationUserRoleCloudFunction = httpsCallable<{
  userId: string;
  organizationId: string;
  role: UserRole;
}>(functions, 'changeOrganizationUserRole');
